import { Check, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { fFecha, fFechaCorta, lifecycle, proximoPaso, type TipoPaso } from '@/components/rdt/rdt-domain';
import { CONN_DONE_CLASS, STEP_COLOR_CLASS } from '@/components/rdt/ciclo-vida-colores';
import { useMe } from '@/hooks/use-me';
import type { RdtDetalle } from '@/types/api';

// Paleta B de los pasos (STEP_COLOR_CLASS / CONN_DONE_CLASS): se mudó a
// ciclo-vida-colores.ts (Avivador v2, T3a) para compartirla con el ciclo
// estático de la card del mosaico (components/avivador/ciclo-estatico.tsx).

// El primer step del ciclo abre un paso DISTINTO según el tipo de RDT:
// "salto" (Provisoria) abre el form de Chequeo de índices, "calculado"
// (Definitiva) abre el de Monto informado — el resto de las keys ('entrega',
// 'aprobado', 'poliza', 'facturado') son todos hitos, se resuelven con el
// fallback 'hito' + su propia key como hitoKey.
const PRIMER_PASO_TIPO: Partial<Record<string, Exclude<TipoPaso, 'comentario'>>> = {
  salto: 'chequeo',
  calculado: 'monto',
};

interface Props {
  rdt: RdtDetalle;
  onAbrirPaso: (tipo: Exclude<TipoPaso, 'comentario'>, hitoKey?: string) => void;
  /** contrato B1: scroll+resalta el ítem del hilo con esta clave (puntos 5/9)
   *  — opcional para no romper mientras el llamador no lo cablea todavía. */
  onIrAItem?: (clave: string) => void;
}

// R3 (plan_migracion_reui.md, Ajuste 29): se QUEDA PROPIO — el Stepper de
// ReUI es `role=tablist` con roving tabindex y anunciaría «pestaña 3 de 5»
// sin panel asociado, semántica incorrecta para un hito de RDT. Es hermano
// del ciclo estático de la card del mosaico (§8 del plan); se le cuenta a
// Juan en QA-3. Solo se le sacó el anillo a mano (el foco lo da la regla
// global `:focus-visible` de index.css) sin cambiar el diseño.
//
// Stepper hecho a mano (sin librería) del ciclo de vida canónico: Saltó →
// Entrega (XOR, itálica mientras no se elige rama) → Aprobado → [Póliza] →
// Facturado. El knob punteado del PRÓXIMO paso accionable abre su formulario
// en Actividad — un solo lugar de escritura (MVP §5.2/§12.2). Los pasos YA
// completados son clickeables (punto 9): saltan al ítem del hilo que los
// originó, con tooltip de fecha/autor/nota (punto 5) — si el paso es un
// hueco histórico sin rastro en el hilo (migradas), avisan con un toast en
// vez de intentar scrollear a ningún lado.
export function CicloVida({ rdt, onAbrirPaso, onIrAItem }: Props) {
  const steps = lifecycle(rdt);
  // Sin knob accionable para viewers (los endpoints de escritura no les
  // están habilitados — el paso pendiente se muestra como punto plano, igual
  // que en una archivada) ni sobre RDTs archivadas.
  const { data: me } = useMe();
  const puedeEscribir = me?.rol === 'gestor' || me?.rol === 'admin';
  // Verificado para R1 (Juan, 2026-09-14): `nextKey` es SIEMPRE el primer
  // step no-`done` (proximoPaso recorre `steps` en orden) y "entrega" va
  // DESPUÉS de "salto"/"calculado" en ese arreglo (ver lifecycle() más
  // arriba) — así que mientras el salto/cálculo no esté hecho, `nextKey`
  // nunca puede valer 'entrega'. Un step que no es `done` ni es `nextKey`
  // se renderiza más abajo como el punto plano sin onClick (rama final del
  // `steps.map`, sin Tooltip/botón) — el knob de "Entrega" YA es inaccionable
  // antes del salto, sin necesidad de sumarle una guarda extra acá.
  const nextKey = rdt.archivada || !puedeEscribir ? null : proximoPaso(steps);

  return (
    <div className="flex items-start justify-start overflow-x-auto pb-1">
      {steps.map((st, i) => {
        const isNext = st.key === nextKey;
        const nextDone = i < steps.length - 1 && steps[i + 1]!.done;
        return (
          // rc.6 (context/qa_visual_reui_2026-09-28.md U-7, decisión de Juan
          // del 28/9): `flex-[1_1_150px]` reparte el sobrante entre los pasos
          // (`grow:1`, más robusto con 5 pasos que `justify-center`). Con
          // `flex-[0_1_150px]` los pasos quedaban empaquetados a la
          // izquierda en cuanto el contenedor superaba 4×150px, que con la
          // barra lateral plegada es el caso normal de la ficha. `min-w-[96px]`
          // + `overflow-x-auto` del contenedor siguen cubriendo la card
          // angosta (barra desplegada, ~495px).
          <div key={st.key} className="relative flex min-w-[96px] flex-[1_1_150px] flex-col items-center gap-1.5 text-center">
            {i < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={cn('absolute top-[10px] h-0.5 rounded-full bg-border-strong', nextDone && CONN_DONE_CLASS[st.color])}
                style={{ left: 'calc(50% + 15px)', right: 'calc(-50% + 15px)' }}
              />
            )}

            {st.done ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      type="button"
                      onClick={() => (st.claveHilo ? onIrAItem?.(st.claveHilo) : toast('Paso del histórico — sin rastro en el hilo'))}
                      aria-label={`Ver ${st.nombre} en el hilo`}
                      className={cn(
                        'z-10 flex h-[22px] w-[22px] items-center justify-center rounded-full border-2',
                        STEP_COLOR_CLASS[st.color],
                      )}
                    />
                  }
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </TooltipTrigger>
                <TooltipContent>
                  {/* Autor/fecha SIEMPRE (decisión: también en pasos sin
                      nota); la nota va abajo solo si existe. */}
                  <span className="flex flex-col gap-0.5">
                    <span>
                      {fFecha(st.fecha) ?? '—'} · {st.autor ?? 'Histórico'}
                    </span>
                    {st.nota && <span className="text-background/75">{st.nota}</span>}
                  </span>
                </TooltipContent>
              </Tooltip>
            ) : isNext ? (
              <span className="relative z-10 flex h-[22px] w-[22px] items-center justify-center">
                {/* Pulso idle bien sutil (2.5s, opacidad baja) para que el
                    próximo paso accionable "despierte" sin ser frenético. */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-full bg-accent-strong/30 [animation-duration:2.5s] motion-safe:animate-ping"
                />
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        onClick={() => onAbrirPaso(PRIMER_PASO_TIPO[st.key] ?? 'hito', PRIMER_PASO_TIPO[st.key] ? undefined : st.key)}
                        aria-label={`Registrar ${st.nombre}`}
                        className="relative z-10 flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded-full border-2 border-dashed border-primary bg-primary/10 text-accent-strong transition-all duration-150 hover:scale-110 hover:border-solid hover:border-primary-solid hover:bg-primary-solid hover:text-primary-solid-foreground hover:shadow-[0_0_0_4px_color-mix(in_srgb,var(--primary)_18%,transparent)]"
                      />
                    }
                  >
                    <Plus className="h-3 w-3" strokeWidth={2.6} />
                  </TooltipTrigger>
                  <TooltipContent>{`Completar ${st.nombre}`}</TooltipContent>
                </Tooltip>
              </span>
            ) : (
              <span className="z-10 flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 border-border-strong bg-surface-card" />
            )}

            <span
              className={cn(
                'max-w-[15ch] text-[11.5px] font-[550] leading-[1.25] text-text-tertiary text-balance',
                // El nombre del paso "salto" (Saltó/Calculado) en light usa
                // --salta en vez de --foreground: accent-strong (el color de
                // fondo del círculo) falla AA a este tamaño de texto (punto
                // 15, Paleta B) — el resto de los pasos done quedan igual.
                st.done && (st.color === 'salto' ? 'text-salta' : 'text-foreground'),
                st.fork && 'italic',
              )}
            >
              {st.nombre}
            </span>

            {st.done ? (
              <span className="text-[10.5px] tabular-nums text-muted-foreground">
                {[fFechaCorta(st.fecha), st.sub].filter(Boolean).join(' · ')}
                {/* Marca «(editado)» (BACKLOG «Hitos editados sin marca
                    (editado)», Juan #15): fecha corregida vía "Corregir
                    hitos" — mismo sufijo que usan chequeos/hilo. */}
                {st.editado && <span className="italic"> (editado)</span>}
              </span>
            ) : isNext ? (
              // Cubre tablet (sin hover): el texto ya avisa que es accionable.
              <span className="text-[10.5px] font-[550] tabular-nums text-accent-strong">Completar →</span>
            ) : (
              <span className="text-[10.5px] tabular-nums text-text-tertiary">
                {st.forkSub ? `${st.forkSub} · pendiente` : 'Pendiente'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
